import React, { useState, useRef, useEffect } from 'react';

export default function MultiSelectDropdown({
    options = [], // string array
    value = [],   // string array of selected items
    onChange,     // (newValues) => void
    placeholder = "Select options...",
    className = ""
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt =>
        opt.toLowerCase().includes(search.toLowerCase())
    );

    const toggleOption = (opt) => {
        if (value.includes(opt)) {
            onChange(value.filter(v => v !== opt));
        } else {
            onChange([...value, opt]);
        }
    };

    const clearAll = (e) => {
        e.stopPropagation();
        onChange([]);
        // Optional: clear search on clear? 
        // setSearch(""); 
    };

    const selectAll = (e) => {
        e.stopPropagation();
        onChange([...options]);
    };

    // Calculate display text
    let displayText = placeholder;
    if (value.length === 1) {
        displayText = value[0].length > 25 ? value[0].substring(0, 22) + '...' : value[0];
    } else if (value.length > 1) {
        if (value.length === options.length && options.length > 0) {
            displayText = "All Selected";
        } else {
            displayText = `${value.length} Selected`;
        }
    }

    return (
        <div ref={dropdownRef} className={`relative flex items-center min-w-[12.5em] ${className}`}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between bg-transparent text-[0.625rem] font-black uppercase tracking-widest outline-none cursor-pointer text-(--accent) text-left py-1"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className="truncate pr-4">{displayText}</span>
                <span className="flex items-center gap-2">
                    {value.length > 0 && (
                        <span
                            onClick={clearAll}
                            className="text-[0.5625rem] hover:text-(--error) transition-colors text-(--text-muted)"
                            title="Clear selections"
                        >
                            ✕
                        </span>
                    )}
                    <span className="text-[0.75rem] leading-none mb-1 text-(--text-muted)">{isOpen ? '▲' : '▼'}</span>
                </span>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 z-50 w-64 mt-2 bg-(--surface-elevated) border-2 border-(--border) shadow-[0.25em_0.25em_0em_var(--border)] max-h-80 flex flex-col">
                    {/* Search Input */}
                    <div className="p-2 border-b-2 border-(--border)">
                        <input
                            type="text"
                            className="w-full bg-(--background) text-(--text) text-[0.625rem] font-mono p-2 border border-(--border) outline-none focus:border-(--accent) transition-colors placeholder-(--text-muted)"
                            placeholder="SEARCH..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    {/* Quick Actions */}
                    {options.length > 0 && (
                        <div className="px-2 py-1 flex justify-between border-b border-(--border)/30 bg-(--background)/50">
                            <button
                                onClick={selectAll}
                                className="text-[0.5rem] font-black uppercase tracking-widest text-(--text-muted) hover:text-(--accent) py-1 transition-colors"
                            >
                                Select All
                            </button>
                            <button
                                onClick={clearAll}
                                className="text-[0.5rem] font-black uppercase tracking-widest text-(--text-muted) hover:text-(--error) py-1 transition-colors"
                            >
                                Clear All
                            </button>
                        </div>
                    )}

                    {/* Options List */}
                    <div className="overflow-y-auto flex-1 p-2 space-y-1">
                        {filteredOptions.length === 0 ? (
                            <div className="p-4 text-center text-[0.5625rem] font-black uppercase tracking-widest text-(--text-muted)">
                                No matches found
                            </div>
                        ) : (
                            filteredOptions.map((opt) => {
                                const isSelected = value.includes(opt);
                                return (
                                    <label
                                        key={opt}
                                        onClick={(e) => { e.stopPropagation(); toggleOption(opt); }}
                                        className="flex items-center gap-2 p-2 hover:bg-(--primary) cursor-pointer group transition-colors"
                                    >
                                        <div className={`w-3 h-3 flex items-center justify-center border transition-colors ${isSelected ? 'bg-(--accent) border-(--accent)' : 'border-(--border) group-hover:border-(--accent)'}`}>
                                            {isSelected && <span className="text-white text-[0.5rem] block mt-[1px]">✓</span>}
                                        </div>
                                        <span className="text-[0.5625rem] font-mono text-(--text) truncate" title={opt}>
                                            {opt}
                                        </span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
